import { Alert, Button, Card, DatePicker, Dropdown, Space, Table, Tag, Typography, App as AntApp } from "antd";
import { LeftOutlined, MoreOutlined, RightOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs, { type Dayjs } from "dayjs";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../app/api.ts";
import { ApiError } from "../app/apiError.ts";
import { useClassAttendance, type ClassLessonAttendance } from "../app/attendance.ts";
import { useAuth } from "../app/auth.tsx";
import { formatDate } from "../app/classes.ts";
import { useClassGrades } from "../app/grading.ts";
import { initialWeek, lessonKeys, useShowLessonWarnings, weekStart, type LessonDetails, type LessonStatus } from "../app/lessons.ts";
import { shortTime } from "../app/timetable.ts";
import { LessonActionModal, type LessonAction } from "./LessonActionModal.tsx";
import { LessonAttendanceModal } from "./LessonAttendanceModal.tsx";
import { LessonGradesModal } from "./LessonGradesModal.tsx";
import { LessonHistoryModal } from "./LessonHistoryModal.tsx";

interface Props {
  classId: string;
  yearStart: string;
  yearEnd: string;
  canManage: boolean;
}

const DAYS_PER_WEEK = 7;
const statusColors: Record<LessonStatus, string> = { Planned: "green", Moved: "blue", Cancelled: "red" };

/**
 * The class's generated lessons for one week, cancelled ones included and tagged, with each lesson's change history and, for
 * users who manage the academic structure, the lesson changes (cancel, move, set or clear a substitute, restore a cancelled one).
 */
export function ClassLessonsCard({ classId, yearStart, yearEnd, canManage }: Props) {
  const { t } = useTranslation();
  const { message, modal } = AntApp.useApp();
  const queryClient = useQueryClient();
  const showWarnings = useShowLessonWarnings();
  const [week, setWeek] = useState<Dayjs>(() => initialWeek(yearStart, yearEnd));
  const [selected, setSelected] = useState<{ lesson: LessonDetails; action: LessonAction }>();
  const [historyOf, setHistoryOf] = useState<LessonDetails>();
  const [attendanceOf, setAttendanceOf] = useState<LessonDetails>();
  const [gradesOf, setGradesOf] = useState<LessonDetails>();
  const from = week.format("YYYY-MM-DD");
  const to = week.add(DAYS_PER_WEEK - 1, "day").format("YYYY-MM-DD");
  const { hasPermission } = useAuth();
  const canReadAttendance = hasPermission("attendance.read");
  // Without attendance.readAll (a teacher) the server returns, and lets them open, only their own lessons.
  const mayOpenAttendance = (lesson: LessonDetails) => canReadAttendance && (hasPermission("attendance.readAll") || attendance.data?.has(lesson.id) === true);
  const attendance = useClassAttendance(classId, from, to, canReadAttendance);
  // Grades follow the same rule: without grading.readAll the server lists, and lets the caller open, only their own lessons.
  const canReadGrades = hasPermission("grading.read");
  const grades = useClassGrades(classId, from, to, canReadGrades);
  const mayOpenGrades = (lesson: LessonDetails) => canReadGrades && lesson.status !== "Cancelled" && grades.data?.has(lesson.id) === true;

  const lessons = useQuery({
    queryKey: lessonKeys.week(classId, from),
    queryFn: async () => {
      const { data, error } = await api.GET("/academic/classes/{id}/lessons", { params: { path: { id: classId }, query: { From: from, To: to, IncludeCancelled: true } } });
      if (error || !data) throw new Error(t("app.error"));
      return data;
    },
  });

  const removeSubstitute = useMutation({
    mutationFn: async (lesson: LessonDetails) => {
      const { data, error, response } = await api.DELETE("/academic/lessons/{id}/substitute", { params: { path: { id: lesson.id } } });
      if (!data) throw new ApiError(response.status, error);
      return data;
    },
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: lessonKeys.all(classId) });
      showWarnings(saved.warnings);
    },
    onError: (err) => void message.error((err instanceof ApiError ? err.messages(t("app.error")) : [t("app.error")]).join(" ")),
  });

  const confirmRemoveSubstitute = (lesson: LessonDetails) =>
    modal.confirm({
      title: t("lessons.removeSubstituteConfirm", { name: lesson.teacherName ?? "" }),
      okText: t("lessons.removeSubstitute"),
      cancelText: t("family.cancel"),
      onOk: () => removeSubstitute.mutateAsync(lesson),
    });

  const restore = useMutation({
    mutationFn: async (lesson: LessonDetails) => {
      const { data, error, response } = await api.POST("/academic/lessons/{id}/restore", { params: { path: { id: lesson.id } } });
      if (!data) throw new ApiError(response.status, error);
      return data;
    },
    onSuccess: async (restored) => {
      await queryClient.invalidateQueries({ queryKey: lessonKeys.all(classId) });
      void message.success(t("lessons.restored"));
      showWarnings(restored.warnings);
    },
    onError: (err) => void message.error((err instanceof ApiError ? err.messages(t("app.error")) : [t("app.error")]).join(" ")),
  });

  const lessonLabel = (lesson: LessonDetails) =>
    t("lessons.lessonLabel", { subject: lesson.subjectName, date: formatDate(lesson.date), period: Number(lesson.period) });

  const confirmRestore = (lesson: LessonDetails) =>
    modal.confirm({
      title: t("lessons.restoreConfirm", { lesson: lessonLabel(lesson) }),
      okText: t("lessons.restore"),
      cancelText: t("family.cancel"),
      onOk: () => restore.mutateAsync(lesson).catch(() => undefined),
    });

  const changeItems = (lesson: LessonDetails) =>
    lesson.status === "Cancelled"
      ? [{ key: "restore", label: t("lessons.restore") }]
      : [
          { key: "move", label: t("lessons.move") },
          { key: "substitute", label: lesson.isSubstituted ? t("lessons.changeSubstitute") : t("lessons.setSubstitute") },
          ...(lesson.isSubstituted ? [{ key: "removeSubstitute", label: t("lessons.removeSubstitute") }] : []),
          { key: "cancel", label: t("lessons.cancel"), danger: true },
        ];

  const actionItems = (lesson: LessonDetails) => [
    ...(canManage ? [...changeItems(lesson), { type: "divider" as const }] : []),
    ...(mayOpenAttendance(lesson) ? [{ key: "attendance", label: t("attendance.open") }] : []),
    ...(mayOpenGrades(lesson) ? [{ key: "grades", label: t("grading.open") }] : []),
    { key: "history", label: t("lessons.history") },
  ];

  const onAction = (lesson: LessonDetails, key: string) => {
    if (key === "attendance") setAttendanceOf(lesson);
    else if (key === "grades") setGradesOf(lesson);
    else if (key === "removeSubstitute") confirmRemoveSubstitute(lesson);
    else if (key === "restore") confirmRestore(lesson);
    else if (key === "history") setHistoryOf(lesson);
    else setSelected({ lesson, action: key as LessonAction });
  };

  const columns = [
    {
      title: t("lessons.date"),
      key: "date",
      render: (_: unknown, l: LessonDetails) => formatDate(l.date),
    },
    {
      title: t("timetable.period"),
      key: "period",
      render: (_: unknown, l: LessonDetails) => (
        <Space orientation="vertical" size={0}>
          <span>{t("timetable.periodNumber", { period: Number(l.period) })}</span>
          <Typography.Text type="secondary">{`${shortTime(l.startTime)}–${shortTime(l.endTime)}`}</Typography.Text>
        </Space>
      ),
    },
    { title: t("lessons.subject"), dataIndex: "subjectName", key: "subject" },
    {
      title: t("lessons.teacher"),
      key: "teacher",
      render: (_: unknown, l: LessonDetails) => (
        <Space orientation="vertical" size={0}>
          <span>{l.teacherName ?? t("lessons.noTeacher")}</span>
          {l.isSubstituted && (
            <Typography.Text type="secondary">
              <Tag color="purple">{t("lessons.substituteTag")}</Tag>
              {t("lessons.insteadOf", { name: l.assignedTeacherName ?? t("lessons.noTeacher") })}
            </Typography.Text>
          )}
        </Space>
      ),
    },
    {
      title: t("lessons.status"),
      key: "status",
      render: (_: unknown, l: LessonDetails) => (
        <Space orientation="vertical" size={0}>
          <Tag color={statusColors[l.status]}>{t(`lessons.statuses.${l.status}`)}</Tag>
          {l.status === "Moved" && l.originalDate && (
            <Typography.Text type="secondary">{t("lessons.movedFrom", { date: formatDate(l.originalDate), period: Number(l.originalPeriod) })}</Typography.Text>
          )}
          {l.status === "Cancelled" && l.cancellationCause && (
            <Typography.Text type="secondary">{`${t(`lessons.causes.${l.cancellationCause}`)}: ${l.cancellationReason ?? ""}`}</Typography.Text>
          )}
        </Space>
      ),
    },
    ...(canReadAttendance
      ? [{ title: t("attendance.column"), key: "attendance", render: (_: unknown, l: LessonDetails) => attendanceSummary(attendance.data?.get(l.id)) }]
      : []),
    {
      title: t("lessons.actions"),
      key: "actions",
      render: (_: unknown, l: LessonDetails) => (
        <Dropdown menu={{ items: actionItems(l), onClick: ({ key }) => onAction(l, key) }} trigger={["click"]}>
          <Button type="text" icon={<MoreOutlined />} aria-label={t("lessons.actionsFor", { subject: l.subjectName, date: formatDate(l.date) })} />
        </Dropdown>
      ),
    },
  ];

  function attendanceSummary(a: ClassLessonAttendance | undefined) {
    if (!a || a.lessonStatus === "Cancelled") return null;
    if (Number(a.markedCount) === 0) return <Typography.Text type={a.isPastDeadline ? "danger" : "secondary"}>{t("attendance.notMarked")}</Typography.Text>;
    return t("attendance.summary", { present: Number(a.present) + Number(a.late), roster: Number(a.rosterCount) });
  }

  const yearStartWeek = weekStart(dayjs(yearStart));
  const yearEndWeek = weekStart(dayjs(yearEnd));

  return (
    <Card title={t("lessons.title")}>
      <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
        <Space wrap>
          <Button icon={<LeftOutlined />} aria-label={t("lessons.previousWeek")} disabled={!week.isAfter(yearStartWeek, "day")} onClick={() => setWeek(week.subtract(DAYS_PER_WEEK, "day"))} />
          <DatePicker
            aria-label={t("lessons.week")}
            format={() => t("lessons.weekRange", { from: formatDate(from), to: formatDate(to) })}
            allowClear={false}
            value={week}
            onChange={(d: Dayjs | null) => d && setWeek(weekStart(d))}
          />
          <Button icon={<RightOutlined />} aria-label={t("lessons.nextWeek")} disabled={!week.isBefore(yearEndWeek, "day")} onClick={() => setWeek(week.add(DAYS_PER_WEEK, "day"))} />
        </Space>
        {lessons.isError && <Alert type="error" showIcon title={t("app.error")} />}
        <Table<LessonDetails>
          rowKey="id"
          size="small"
          columns={columns}
          dataSource={lessons.data ?? []}
          loading={lessons.isPending}
          pagination={false}
          locale={{ emptyText: t("lessons.empty") }}
          scroll={{ x: "max-content" }}
        />
      </Space>
      <LessonActionModal
        classId={classId}
        lesson={selected?.lesson}
        action={selected?.action ?? "cancel"}
        yearStart={yearStart}
        yearEnd={yearEnd}
        onClose={() => setSelected(undefined)}
      />
      <LessonHistoryModal classId={classId} lesson={historyOf} onClose={() => setHistoryOf(undefined)} />
      <LessonAttendanceModal classId={classId} lesson={attendanceOf} onClose={() => setAttendanceOf(undefined)} />
      <LessonGradesModal lesson={gradesOf} onClose={() => setGradesOf(undefined)} />
    </Card>
  );
}
